import { useDealershipSelector } from '@makemydeal/dr-activities-dealership-picker';
import { useDispatch, useSelector } from 'react-redux';
import * as dealValidationSelectors from '../selectors/dealValidationSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useEffect } from 'react';
import { roleAssumptionCreators, useRoleAssumptionSelector } from '@makemydeal/dr-activities-role-assumption';
import { navigateTo } from '@makemydeal/dr-activities-common';
import { RouteValues } from '../routeConstants';
import { shouldClearRoleAssumption, shouldInitiateRoleAssumption } from '../utils/roleAssumptionUtils';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';

/**
 * Custom hook that handles role assumption navigation logic in the Sales Check-in application.
 *
 * This hook monitors various conditions and automatically navigates users when role assumption
 * should be initiated. It handles both single dealership (direct navigation) and multiple
 * dealership (dashboard selection) scenarios.
 *
 * @returns void - This hook only performs side effects, no return value
 */
export const useRoleAssumptionNavigation = () => {
    const dispatch = useDispatch();

    // Fetch dealership data and request status
    const { dealerships, requestStatus } = useDealershipSelector();
    const dealValidation = useSelector(dealValidationSelectors.getDealValidationState);

    const { assumedUserName } = useRoleAssumptionSelector();

    const isEmployee = useSelector(dealValidationSelectors.getIsEmployee);
    const { mvDealValidation, enableRoleAssumptionInMV } = useSelector(featureToggleSelectors.getFeatureToggles);
    const commonOrgId = useSelector(dealValidationSelectors.getInCompleteDealCoOrgId);
    const isQuoteModeEnabled = useSelector(featureToggleSelectors.isManagerViewQuoteModeEnabled);
    // Check if dealership is missing in validation

    useEffect(() => {
        // Check if all conditions are met to initiate role assumption
        if (
            shouldInitiateRoleAssumption(
                isEmployee,
                enableRoleAssumptionInMV!,
                mvDealValidation!,
                dealValidation,
                requestStatus as ApiRequestStatus,
                dealerships,
                assumedUserName
            )
        ) {
            //Filter out dealerships that are assosiated with the current commonOrgId
            const filteredDealerships = dealerships.filter((dealership) =>
                commonOrgId ? dealership.commonOrgId === commonOrgId : false
            );
            //Navigate back to role assumption if no dealerships are found for the commonOrgId
            if (commonOrgId && filteredDealerships.length === 0) {
                dispatch(roleAssumptionCreators.checkNoDealAssociatedWithDealerships(true));
                return dispatch(roleAssumptionCreators.clearRoleAssumption());
            }

            // Continue the flow: under quote mode stay inside Manager View (dashboard, where the
            // in-MV dealership selection lives); otherwise fall back to the Open Deal v2 route.
            dispatch(navigateTo(isQuoteModeEnabled ? RouteValues.DASHBOARD : RouteValues.OPEN_DEAL));

            // Dispatch action to mark role assumption as started
            return dispatch(roleAssumptionCreators.startRoleAssumption());
        }

        if (
            shouldClearRoleAssumption(
                isEmployee,
                enableRoleAssumptionInMV!,
                mvDealValidation!,
                dealValidation,
                requestStatus as ApiRequestStatus,
                dealerships
            )
        ) {
            // Dispatch action to mark role assumption as started
            dispatch(roleAssumptionCreators.checkNoDealAssociatedWithDealerships(false));
            return dispatch(roleAssumptionCreators.clearRoleAssumption());
        }
    }, [
        // Dependencies that trigger re-evaluation of role assumption conditions
        dealerships,
        enableRoleAssumptionInMV,
        isEmployee,
        isQuoteModeEnabled
    ]);
};
